/**
 * FileUploader Component
 * Drag-and-drop file upload with progress indicator.
 */

import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, File, X, AlertCircle } from 'lucide-react';

interface FileUploaderProps {
  onFileSelected: (file: File) => void;
  acceptedFormats?: string[];
  maxSizeMB?: number;
  isDisabled?: boolean;
  label?: string;
}

const DEFAULT_FORMATS = ['.mp3', '.wav', '.m4a', '.webm', '.mp4', '.ogg', '.flac'];
const DEFAULT_MAX_SIZE = 100; // MB

export const FileUploader: React.FC<FileUploaderProps> = ({
  onFileSelected,
  acceptedFormats = DEFAULT_FORMATS,
  maxSizeMB = DEFAULT_MAX_SIZE,
  isDisabled = false,
  label,
}) => {
  const { t } = useTranslation();
  const resolvedLabel = label || t('fileUpload.defaultLabel');
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback((file: File): string | null => {
    // Check file extension
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!acceptedFormats.includes(extension)) {
      return t('fileUpload.invalidFormat', { formats: acceptedFormats.join(', ') });
    }

    // Check file size
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > maxSizeMB) {
      return t('fileUpload.fileTooLarge', { maxSize: maxSizeMB });
    }

    return null;
  }, [acceptedFormats, maxSizeMB, t]);

  const handleFile = useCallback((file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      setSelectedFile(null);
      return;
    }

    setError(null);
    setSelectedFile(file);
    onFileSelected(file);
  }, [validateFile, onFileSelected]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDisabled) {
      setIsDragging(true);
    }
  }, [isDisabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (isDisabled) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFile(files[0]);
    }
  }, [isDisabled, handleFile]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  }, [handleFile]);

  const handleClick = useCallback(() => {
    if (!isDisabled) {
      fileInputRef.current?.click();
    }
  }, [isDisabled]);

  const handleRemoveFile = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="w-full">
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedFormats.join(',')}
        onChange={handleFileInputChange}
        className="hidden"
        disabled={isDisabled}
      />

      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          relative border-2 border-dashed rounded-xl p-4 sm:p-8 text-center cursor-pointer
          transition-all duration-200
          ${isDisabled
            ? 'bg-slate-100 border-slate-300 cursor-not-allowed opacity-60'
            : isDragging
              ? 'bg-blue-50 border-blue-400 scale-[1.02]'
              : selectedFile
                ? 'bg-indigo-50 border-indigo-400'
                : 'bg-slate-50 border-slate-300 hover:border-blue-400 hover:bg-blue-50/50'
          }
        `}
      >
        {selectedFile ? (
          <div className="flex items-center justify-center gap-4">
            <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
              <File className="w-6 h-6 text-indigo-600" />
            </div>
            <div className="text-left">
              <p className="font-medium text-slate-800 truncate max-w-[200px]">
                {selectedFile.name}
              </p>
              <p className="text-sm text-slate-500">
                {formatFileSize(selectedFile.size)}
              </p>
            </div>
            <button
              onClick={handleRemoveFile}
              className="ml-4 p-2 rounded-full hover:bg-slate-200 transition-colors"
              disabled={isDisabled}
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        ) : (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
              <Upload className={`w-8 h-8 ${isDragging ? 'text-blue-600' : 'text-blue-500'}`} />
            </div>
            <p className="text-lg font-medium text-slate-700 mb-2">
              {isDragging ? t('fileUpload.dropHere') : resolvedLabel}
            </p>
            <p className="text-sm text-slate-500 mb-4">
              {t('fileUpload.orClickToBrowse')}
            </p>
            <p className="text-xs text-slate-400">
              {t('fileUpload.supported', { formats: acceptedFormats.join(', '), maxSize: maxSizeMB })}
            </p>
          </>
        )}
      </div>

      {error && (
        <div className="mt-3 flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};
